import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { X, Clock, Eye, ExternalLink, Calendar } from 'lucide-react';
import { Contenuto, StatoContenuto } from '../types';
import { useData } from '../contexts/DataContext';
import './Modal.css';

interface ContentSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date;
  contents: Contenuto[];
  onSelectContent?: (content: Contenuto) => void;
  onManageContent?: (content: Contenuto) => void;
}

const ContentSelectionModal: React.FC<ContentSelectionModalProps> = ({
  isOpen,
  onClose,
  date,
  contents,
  onSelectContent,
  onManageContent
}) => {
  const { getClienteById } = useData();
  const modalRef = useRef<HTMLDivElement>(null);
  const scrollYRef = useRef(0);
  const [isClosing, setIsClosing] = useState(false);

  // Blocca lo scroll del body e alla chiusura riporta la pagina al punto
  // esatto da cui il modale era stato aperto (posizione tenuta in un ref:
  // rileggerla da document.body.style.top non funziona, la cleanup la azzera
  // prima).
  useEffect(() => {
    if (!isOpen) return;

    scrollYRef.current = window.scrollY;
    document.body.classList.add('modal-open');
    document.body.style.top = `-${scrollYRef.current}px`;

    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollYRef.current);
    };
  }, [isOpen]);

  // Handle escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Handle keyboard visibility on mobile
  useEffect(() => {
    if (!isOpen) return;

    const handleViewportResize = () => {
      if (modalRef.current && window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;

        if (viewportHeight < windowHeight * 0.75) {
          modalRef.current.style.maxHeight = `${viewportHeight - 20}px`;
        } else {
          modalRef.current.style.maxHeight = '';
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      return () => window.visualViewport?.removeEventListener('resize', handleViewportResize);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (window.innerWidth < 640) {
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        onClose();
      }, 250);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  const getStatusStyle = (stato: StatoContenuto) => {
    const styles: Record<StatoContenuto, string> = {
      [StatoContenuto.BOZZA]: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
      [StatoContenuto.TESTO_PRONTO]: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      [StatoContenuto.REGISTRATO]: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      [StatoContenuto.MONTATO]: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
      [StatoContenuto.PROGRAMMATO]: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
      [StatoContenuto.PUBBLICATO]: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      [StatoContenuto.ARCHIVIATO]: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
    return styles[stato] || 'bg-slate-100 text-slate-700';
  };

  const getStatusText = (stato: StatoContenuto) => {
    const texts: Record<StatoContenuto, string> = {
      [StatoContenuto.BOZZA]: 'Bozza',
      [StatoContenuto.TESTO_PRONTO]: 'Testo confermato',
      [StatoContenuto.REGISTRATO]: 'Registrato',
      [StatoContenuto.MONTATO]: 'Montato',
      [StatoContenuto.PROGRAMMATO]: 'Programmato',
      [StatoContenuto.PUBBLICATO]: 'Pubblicato',
      [StatoContenuto.ARCHIVIATO]: 'Archiviato',
    };
    return texts[stato] || stato;
  };

  const isPastDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const formatTime = (dateString: Date) => {
    return format(new Date(dateString), 'HH:mm');
  };

  return (
    <div className="modal-container" role="dialog" aria-modal="true">
      <div
        className={`modal-backdrop ${isClosing ? 'opacity-0 transition-opacity duration-250' : ''}`}
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        ref={modalRef}
        className={`modal-content modal-size-lg ${isClosing ? 'animate-slide-down' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={isClosing ? { animation: 'modalSlideDown 0.25s ease-out forwards' } : undefined}
      >
        {/* Header - Sticky */}
        <div className="modal-header">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 sm:w-10 sm:h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="modal-title truncate">
                {format(date, 'dd MMMM yyyy', { locale: it })}
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {contents.length} {contents.length === 1 ? 'contenuto' : 'contenuti'}
                {isPastDate() && ' (pubblicati)'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="modal-close-button flex-shrink-0"
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content List - Scrollable */}
        <div className="modal-body">
          <div className="space-y-3">
            {contents.map((content, index) => {
              const cliente = getClienteById(content.clienteId);
              const canManageScheduling = onManageContent && (
                content.stato === StatoContenuto.MONTATO ||
                content.stato === StatoContenuto.PROGRAMMATO
              );

              return (
                <div
                  key={content.id}
                  className={`
                    p-4 rounded-xl border transition-all
                    ${onSelectContent ? 'cursor-pointer active:scale-[0.98]' : ''}
                    ${isPastDate()
                      ? 'bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                      : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-600 hover:shadow-sm'
                    }
                  `}
                  onClick={() => onSelectContent?.(content)}
                >
                  {/* Top row */}
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className="text-xs font-medium text-slate-400 dark:text-slate-500 flex-shrink-0">
                        #{index + 1}
                      </span>
                      {content.dataPubblicazione && (
                        <div className="flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                          <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                          <span>{formatTime(content.dataPubblicazione)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusStyle(content.stato)}`}>
                        {getStatusText(content.stato)}
                      </span>
                      {onSelectContent && (
                        <Eye className="h-4 w-4 text-slate-300 dark:text-slate-600 hidden sm:block" />
                      )}
                    </div>
                  </div>

                  {/* Client name */}
                  {cliente && (
                    <p className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400 mb-1">
                      {cliente.nomeAzienda}
                    </p>
                  )}

                  {/* Title */}
                  <h3 className="font-medium text-slate-900 dark:text-slate-100 mb-2 line-clamp-2 sm:line-clamp-1 text-sm sm:text-base">
                    {content.titolo || 'Contenuto senza titolo'}
                  </h3>

                  {/* Preview */}
                  {(content.hook || content.body) && (
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 line-clamp-2 mb-3">
                      {content.hook || content.body}
                    </p>
                  )}

                  {/* Links */}
                  {(content.linkMontaggio || content.linkPubblicato) && (
                    <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-3">
                      {content.linkMontaggio && (
                        <a
                          href={content.linkMontaggio}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 py-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          <span>Video montato</span>
                        </a>
                      )}
                      {content.linkPubblicato && (
                        <a
                          href={content.linkPubblicato}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 py-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          <span>Link pubblicato</span>
                        </a>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  {content.note && (
                    <div className="p-3 bg-slate-50 dark:bg-gray-700/50 rounded-lg text-xs text-slate-600 dark:text-slate-400">
                      <strong>Note:</strong> {content.note}
                    </div>
                  )}

                  {/* Manage button */}
                  {canManageScheduling && (
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onManageContent?.(content);
                          onClose();
                        }}
                        className="modal-btn-primary text-xs min-h-[44px] sm:min-h-[36px] px-4"
                      >
                        <Calendar className="h-4 w-4" />
                        <span className="hidden sm:inline">Gestisci programmazione</span>
                        <span className="sm:hidden">Gestisci</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer - Sticky */}
        <div className="modal-footer">
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 flex-1 hidden sm:block">
            {isPastDate()
              ? 'Contenuti pubblicati'
              : 'Seleziona un contenuto per i dettagli'
            }
          </p>
          <button
            onClick={handleClose}
            className="modal-btn-secondary min-h-[48px] sm:min-h-[40px] flex-1 sm:flex-none"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContentSelectionModal;
